import cn from 'classnames'
import React, {
  ComponentProps,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import ErrorList from 'src/components/ErrorList/ErrorList'

import style from './RadioGroup.style'

export type RadioButtonProps<K extends string> = {
  className?: string
  value: K
  title: string
  description?: string
  extraSelectedContent?: React.ReactNode
  isInvalid?: boolean
  testId?: string
} & ComponentProps<'input'>

/** For use inside of `RadioGroup`. */
function RadioButton<K extends string>(
  props: RadioButtonProps<K>,
): ReactElement {
  const {
    className = '',
    title,
    description,
    extraSelectedContent,
    isInvalid,
    testId,
    ...rest
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    // Set css :invalid
    if (isInvalid) {
      inputRef.current?.setCustomValidity('This selection is invalid.')
    } else {
      inputRef.current?.setCustomValidity('')
    }
  }, [isInvalid])

  return (
    <label className={cn('RadioButton', className)} data-testid={testId}>
      <input type="radio" ref={inputRef} {...rest} />
      <div>
        <p className="RadioButton--title">{title}</p>
        <p className="RadioButton--desc">{description}</p>
      </div>
      {/* Putting this inside the <label> is technically an ARIA violation
          because you aren't allowed to nest <label>s, but that's what most
          closely matches the component design. This is confirmed to work in
          the latest Firefox, Chrome, and Safari on 2025-08-12. */}
      {extraSelectedContent && (
        <div className="RadioButton--extra-content">{extraSelectedContent}</div>
      )}
    </label>
  )
}

type RadioGroupProps<K extends string> = {
  className?: string
  name: string
  label: string
  labelTooltip?: React.ReactNode
  required?: boolean
  value: K | null
  onChange: (newValue: K, event: React.ChangeEvent<HTMLInputElement>) => void
  errors?: (React.ReactElement | string)[]
  testId?: string
  options: RadioButtonProps<K>[]
}

function RadioGroup<K extends string>(props: RadioGroupProps<K>): ReactElement {
  const {
    className = '',
    name,
    label,
    labelTooltip,
    required,
    value,
    onChange,
    errors,
    testId,
    options,
  } = props

  const [showErrors, setShowErrors] = useState(errors !== undefined)

  useEffect((): void => {
    setShowErrors(errors !== undefined)
  }, [errors])

  useEffect((): void => {
    setShowErrors(false)
  }, [value])

  return (
    <fieldset
      className={cn('RadioGroup', className)}
      data-testid={testId}
      css={style}
    >
      <legend>
        {label}
        {labelTooltip}
      </legend>
      <div className="RadioGroup--buttons">
        {options.map((props) => (
          <RadioButton
            key={props.value}
            name={name}
            required={required}
            onChange={(event) => onChange(event.target.value as K, event)}
            checked={value === props.value}
            isInvalid={showErrors}
            {...props}
          />
        ))}
      </div>
      {errors !== undefined && showErrors && <ErrorList errors={errors} />}
    </fieldset>
  )
}

export default RadioGroup
